import { Storage } from "aws-amplify";
import * as XLSX from "xlsx";

// Shared helpers for the per-account files under public/files/accounts/<account>/
// and the S3 photo layout, used by both the UPLOAD and LIST tabs.

export const accountPrefix = (account: string) => `files/accounts/${account}/`;
export const LIST_FILE = "LIST_MATCODE.xlsx";
export const UPLOADED_FILE = "UPLOADED_MATCODE.xlsx";
export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Fetches an object via a fresh presigned URL with the browser cache bypassed,
// so a just-overwritten file is read instead of a stale cached copy.
export async function getFreshBlob(key: string): Promise<Blob> {
  const url = (await Storage.get(key, { level: "public" })) as string;
  const resp = await fetch(url, { cache: "no-store" });
  if (!resp.ok) throw new Error(`โหลดไฟล์ไม่สำเร็จ (${resp.status})`);
  return resp.blob();
}

// Reads an .xlsx from S3 (public level) and returns its first sheet as JSON.
export async function loadXlsxRows(
  key: string
): Promise<Record<string, any>[]> {
  const blob = await getFreshBlob(key);
  const buffer = await blob.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { defval: "" });
}

// Reads the account's UPLOADED_MATCODE.xlsx as a Map of matcode -> UPDATED_AT
// (empty if the file is absent).
async function readUploadedMap(account: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  try {
    const rows = await loadXlsxRows(accountPrefix(account) + UPLOADED_FILE);
    for (const r of rows) {
      const code = String(r.MATCODE ?? "").trim();
      if (code) map.set(code, String(r.UPDATED_AT ?? "").trim());
    }
  } catch {
    /* no file yet -> empty map */
  }
  return map;
}

// Writes a MATCODE + UPDATED_AT UPLOADED_MATCODE.xlsx for the account.
async function writeUploadedMap(
  account: string,
  map: Map<string, string>
): Promise<void> {
  const sorted = Array.from(map.keys()).sort();
  const aoa = [
    ["MATCODE", "UPDATED_AT"],
    ...sorted.map((m) => [m, map.get(m) ?? ""]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  await Storage.put(accountPrefix(account) + UPLOADED_FILE, new Blob([out]), {
    level: "public",
    contentType: XLSX_MIME,
  });
}

// Merges matcodes into the account's UPLOADED_MATCODE.xlsx without duplicating
// (union, not append). Each matcode in this batch gets its UPDATED_AT stamped to
// now (UTC / +00:00); matcodes not in the batch keep their existing timestamp.
export async function mergeUploadedMatcodes(
  account: string,
  newMatcodes: string[]
): Promise<{ added: number; total: number }> {
  const map = await readUploadedMap(account);
  if (newMatcodes.length === 0) return { added: 0, total: map.size };

  const before = map.size;
  const now = new Date().toISOString(); // UTC (+00:00), e.g. 2026-08-11T10:30:00.000Z
  for (const m of newMatcodes) {
    const code = m.trim();
    if (code) map.set(code, now); // new entry or refreshed timestamp
  }
  await writeUploadedMap(account, map);
  return { added: map.size - before, total: map.size };
}
